import React, {useMemo, useState} from "react";
import {
	Button,
	Container,
	Dialog, DialogActions,
	DialogTitle,
	Typography
} from "@mui/material";
import {KeywordEntity, sanitize} from "../Entity/KeywordEntity.ts";
import {getScore, saveScore} from "../service/LocalStorageService.ts";
import {ScoreEntity} from "../Entity/ScoreEntity.ts";
import {convert, KeywordSet} from "../Data/KeywordSet.ts";
import {KeywordSetComponent} from "./KeywordSetComponent.tsx";
import {GameState} from "../Entity/GameStateEntity.ts";
import {InputsComponent} from "./InputsComponent.tsx";
import {StatsComponent} from "./StatsComponent.tsx";

interface GameplayComponentProps {
	readonly set: KeywordSet;
}

export function GameplayComponent(props: GameplayComponentProps): React.ReactElement {
	const [gameState, setGameState] = useState<GameState>("pregame");
	const [giveUpModalOpen, setGiveUpModalOpen] = useState<boolean>(false);
	const [keywords, setKeywords] = useState<KeywordEntity[]>(convert(props.set.keywords));
	const count: number = useMemo((): number => keywords.filter(
		(keyword: KeywordEntity): boolean => keyword.guessed).length,
		[keywords]
	);
	const sanitizedKeywords = useMemo(() => keywords.map(keyword => sanitize(keyword.name)),
		[keywords]);

	function handleMatch(keyword: string): boolean {
		const element = sanitizedKeywords.find((e) => e === sanitize(keyword));

		if (!element) {
			return false;
		}
		const entityIndex: number = sanitizedKeywords.findIndex((e) => e === element);
		if (entityIndex === -1 || !keywords[entityIndex]?.hidden) {
			return false;
		}

		keywords[entityIndex] = {...keywords[entityIndex], hidden: false, guessed: true};
		setKeywords([...keywords]);

		return true;
	}

	function handleInit(): void {
		setGameState("running");
	}

	function handleGiveUp(): void {
		setGiveUpModalOpen(true);
	}

	function handleGiveUpModalClose(): void {
		setGiveUpModalOpen(false);
	}

	function handleGameEnd(): void {
		handleGiveUpModalClose();
		keywords.forEach((keyword: KeywordEntity): void => {keyword.hidden = false;});
		const score: ScoreEntity = {
			quizId: props.set.id,
			scorePercent: count / keywords.length,
		}
		saveScore(score);
		setGameState("ended");
	}

	return <>
		<Container maxWidth="sm">
			<Typography variant="h4">
				Best: {getScore(props.set.id).scorePercent?.toFixed(2)}%
			</Typography>
			<InputsComponent
				gameState={gameState}
				onGiveUp={handleGiveUp}
				onInit={handleInit}
				sanitizedKeywords={sanitizedKeywords}
				onMatch={handleMatch}
			/>
			<StatsComponent
				currentCount={count}
				maxCount={keywords.length}
				timerDuration={props.set.time}
				timerRunning={gameState === "running"}
				onElapse={handleGameEnd}
			/>
		</Container>
		<Container maxWidth="xl">
			<KeywordSetComponent keywords={keywords} />
		</Container>
		<Dialog
			open={giveUpModalOpen}
			onClose={handleGiveUpModalClose}
		>
			<DialogTitle id="alert-dialog-title">
				{"Are you sure you want to give up?"}
			</DialogTitle>
			<DialogActions>
				<Button
					onClick={handleGiveUpModalClose}
					variant="outlined"
					color="error">
					Cancel
				</Button>
				<Button
					onClick={handleGameEnd}
					autoFocus
					variant="contained">
					OK
				</Button>
			</DialogActions>
		</Dialog>
	</>
}
